import * as dateUtils from '../../utils/dateUtils';

import {cartConstants} from '../../components/contexts/Cart';

import ApiRequestBuilder from '../ApiService/Request/ApiRequestBuilder';


const API_PATH_CART_BASE = '/carts';
const API_PATH_CARTS = `${API_PATH_CART_BASE}`;
const API_PATH_CART_ACTIVE = `${API_PATH_CART_BASE}/active`;
const API_PATH_CART_CHECKOUT = `${API_PATH_CART_BASE}/:cartId/checkout`;
const API_PATH_CART_DETAILS = `${API_PATH_CART_BASE}/:cartId`;
const API_PATH_CART_ITEMS = `${API_PATH_CART_BASE}/:cartId/items`;


class CartService {

    /**
     * @type {ApiService}
     */
    apiService;


    /**
     * @param apiService {ApiService}
     */
    constructor(apiService) {

        this.apiService = apiService;
    }


    /**
     * @return {Promise<ApiResponse>}
     */
    async getCarts() {

        const cartsRequest = ApiRequestBuilder.get(API_PATH_CARTS)
            .json()
            .withToken()
            .build();

        return this.apiService.executeOrFail(cartsRequest, 'Error loading carts');
    }


    /**
     * @return {Promise<object>} Active cart
     */
    async getActiveCart() {

        const cartRequest = ApiRequestBuilder.get(API_PATH_CART_ACTIVE)
            .json()
            .withToken()
            .build();

        return this.apiService.executeOrFail(cartRequest, 'Error loading active cart');
    }


    /**
     * @param cartId {string} Id of the cart to load
     * @return {Promise<object>} Updated cart
     */
    async getCart(cartId) {

        const cartRequest = ApiRequestBuilder.get(API_PATH_CART_DETAILS)
            .pathParams({cartId})
            .json()
            .withToken()
            .build();

        return this.apiService.executeOrFail(cartRequest, 'Error loading cart');
    }


    /**
     * Updates partial cart header data:
     *  - customerPoNumber
     *
     * @param cartId {string} Cart id to update the header for
     * @param cartHeaderData {object} Cart header data to set
     * @returns {Promise<object>} Updated cart
     */
    async updateCartHeader(cartId, {customerPoNumber}) {

        const updateHeaderRequest = ApiRequestBuilder.put(API_PATH_CART_DETAILS)
            .pathParams({cartId})
            .json()
            .withToken()
            .body({customerPoNumber})
            .build();

        return this.apiService.executeOrFail(updateHeaderRequest, 'Error updating cart header');
    }


    /**
     * Updates partial cart header data:
     *  - customerPoNumber
     *
     * @param cartId {string} Cart id to update the header for
     * @param cartItems {object[]} Cart item to add
     * @returns {Promise<object>} Cart add response with cart and failed items
     */
    async addCartItems(cartId, cartItems) {

        const addCartItemsRequest = ApiRequestBuilder.put(API_PATH_CART_ITEMS)
            .pathParams({cartId})
            .json()
            .withToken()
            .body(cartItems)
            .build();

        const response = await this.apiService.execute(addCartItemsRequest);
        if (response.isSuccessful() || response.isConflict()) {

            return response.getBody();
        }

        throw Error('Error adding cart items');
    }


    /**
     * @param cartId {string} Cart id to update the header for
     * @param cartItemIds {string[]} Cart item ids to remove items for
     * @returns {Promise<object>} Delete items response with updated cart
     */
    async removeCartItems(cartId, cartItemIds) {

        const removeCartItemsRequest = ApiRequestBuilder.delete(API_PATH_CART_ITEMS)
            .pathParams({cartId})
            .json()
            .withToken()
            .body({
                itemIds: cartItemIds
            })
            .build();

        return this.apiService.executeOrFail(removeCartItemsRequest, 'Error removing cart items');
    }


    /**
     * @param cart {object} Cart to check
     * @return {boolean} True if cart is outdated
     */
    isCartOutdated(cart) {

        const {revisedAt} = cart;
        const revisedCheck = dateUtils.parse(revisedAt)
            .plus({minute: cartConstants.CART_VALIDITY_MINUTES});

        return revisedCheck < dateUtils.now();
    }


    /**
     * Loads checkout data including the cart to be checked out.
     *
     * @param cartId {string} Cart ID to load checkout data for
     * @returns {Promise<object>} Cart checkout response
     */
    async loadCartCheckout(cartId) {

        const checkoutApiRequest = ApiRequestBuilder.get(API_PATH_CART_CHECKOUT)
            .pathParams({cartId})
            .withToken()
            .json()
            .build();

        return this.apiService.executeOrFail(checkoutApiRequest, 'Error loading cart checkout');
    }
}

export default CartService;
